/**
 * Per-tab session identity, shared by the SDK's `session.id` span attribute and
 * the Sentry stamper. Replaces the SDK's `defaultSessionProvider`, which keeps
 * its id in a closure with no getter — unreadable from the Sentry side.
 */

/** Unrelated to `TW_SESSION_ID_KEY` in platform-core, which is the auth token. */
export const TELEMETRY_SESSION_KEY = "tw_telemetry_session"

export interface TelemetrySession {
  /** 32 lowercase hex chars, matching OTEL trace-id shape. */
  id: string
  /** Unix seconds; lower bound of the session deep-link's window. */
  startedAt: number
}

const ID_PATTERN = /^[0-9a-f]{32}$/

// `SessionSpanProcessor` asks per span, so don't re-read storage each time.
// Safe: a per-tab id can't change within a page load.
let cached: TelemetrySession | undefined

function generateId(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  )
}

/** Treats a malformed stored entry as absent. */
function asSession(value: unknown): TelemetrySession | undefined {
  if (typeof value !== "object" || value === null) {
    return undefined
  }
  const { id, startedAt } = value as Record<string, unknown>
  if (typeof id !== "string" || !ID_PATTERN.test(id)) {
    return undefined
  }
  if (typeof startedAt !== "number" || !Number.isFinite(startedAt)) {
    return undefined
  }
  return { id, startedAt }
}

// Access can throw (Safari private mode, storage disabled); same as empty here.
function readStoredSession(): TelemetrySession | undefined {
  try {
    const raw = sessionStorage.getItem(TELEMETRY_SESSION_KEY)
    return raw ? asSession(JSON.parse(raw)) : undefined
  } catch {
    return undefined
  }
}

function persistSession(session: TelemetrySession): void {
  try {
    sessionStorage.setItem(TELEMETRY_SESSION_KEY, JSON.stringify(session))
  } catch {
    // Stays in-memory for this page load.
  }
}

/** Reused across reloads in this tab; recreated if storage is unusable. */
export function getTelemetrySession(): TelemetrySession {
  if (cached) {
    return cached
  }
  const stored = readStoredSession()
  if (stored) {
    cached = stored
    return cached
  }
  cached = { id: generateId(), startedAt: Math.floor(Date.now() / 1000) }
  persistSession(cached)
  return cached
}
